import { Button, Group, Modal, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { useState } from 'react';

import { downloadPdf, fetchPdf, inlinePath, savePdf, type FetchedPdf } from '../lib/download';
import { useCredential, useOrg } from '../lib/session';

// A sheet looked at before it is saved.
//
// The PDF routes need the parent's credential, so the browser cannot open
// them as links: the bytes are fetched once and shown from an object URL, in
// a dialog on a wide screen and in a tab of its own on a phone, where a PDF
// in a frame is too small to read. Saving and printing go on from there —
// the dialog has a button for the one, the browser's PDF view for the other.

/** Shows one fetched PDF, with "Herunterladen" and "Schließen". */
export function PdfViewer({
  pdf,
  title,
  onClose,
}: {
  pdf: FetchedPdf | null;
  title: string;
  onClose: () => void;
}) {
  const narrow = useMediaQuery('(max-width: 48em)');
  return (
    <Modal opened={!!pdf} onClose={onClose} title={title} size="80rem" fullScreen={narrow}>
      {pdf ? (
        <Stack gap="sm">
          <iframe
            src={pdf.url}
            title={title}
            style={{ width: '100%', height: '75vh', border: 0 }}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Schließen
            </Button>
            <Button leftSection={<IconDownload size={16} />} onClick={() => savePdf(pdf)}>
              Herunterladen
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}

/**
 * Download and view for a page of PDFs. Render `viewer` once on the page;
 * `view` fills it, or on a phone opens the sheet in a new tab instead.
 */
export function usePdf() {
  const org = useOrg();
  const credential = useCredential();
  const narrow = useMediaQuery('(max-width: 48em)');
  const [shown, setShown] = useState<{ pdf: FetchedPdf; title: string } | null>(null);
  const opts = { orgId: org['X-Org-ID'], credential };
  const fail = (err: unknown) =>
    notifications.show({ color: 'red', message: (err as Error).message });

  const download = (path: string) => downloadPdf(path, opts).catch(fail);

  const view = async (path: string, title: string) => {
    // The tab opens on the tap itself, before the fetch: opened afterwards, a
    // pop-up blocker would take it for one nobody asked for. Where it is
    // blocked anyway, the dialog shows the sheet.
    const tab = narrow ? window.open('', '_blank') : null;
    try {
      const pdf = await fetchPdf(inlinePath(path), opts);
      if (tab) {
        tab.location.href = pdf.url;
        setTimeout(() => URL.revokeObjectURL(pdf.url), 60_000);
        return;
      }
      setShown({ pdf, title });
    } catch (err) {
      tab?.close();
      fail(err);
    }
  };

  const close = () => {
    if (shown) URL.revokeObjectURL(shown.pdf.url);
    setShown(null);
  };

  const viewer = (
    <PdfViewer pdf={shown?.pdf ?? null} title={shown?.title ?? ''} onClose={close} />
  );
  return { download, view, viewer };
}
